import { isMaterialDefinitionType } from 'commons-app/types/material-definition.type';
import { getUUID } from 'commons/utils/string.utils';
import {
    isRangeMode,
    isResultType,
    MaterialDefinition,
    QcLevel,
    ResultType,
} from 'modules/settings/pods/settings-materials/material-definition.vm';
import {
    DEFAULT_LOT_NUMBER_MAX_LENGTH,
    DEFAULT_RESULT_TYPE,
    MaterialDefinitionFormState,
    QcLevelFormItem,
    rangeModeForResultType,
} from 'modules/settings/pods/settings-materials/settings-material-definitions-form/settings-material-definitions-form.model';
import * as API from './material-definitions.api';

const mapQcLevelToPayload = (level: QcLevelFormItem, resultType: ResultType): API.QcLevelPayload => ({
    ...(level.ordinal != null && { ordinal: level.ordinal }),
    name: level.name.trim(),
    ...(resultType === 'QUANTITATIVE' && {
        ...(level.defaultMin != null && { defaultMin: level.defaultMin }),
        ...(level.defaultMax != null && { defaultMax: level.defaultMax }),
        ...(level.defaultTarget != null && { defaultTarget: level.defaultTarget }),
        ...(level.defaultDeviationPercent != null && { defaultDeviationPercent: level.defaultDeviationPercent }),
    }),
    ...(resultType === 'QUALITATIVE' &&
        level.defaultTargetValues != null &&
        level.defaultTargetValues.trim() !== '' && { defaultTargetValues: level.defaultTargetValues }),
});

const mapQcLevelsFromApi = (levels?: API.QcLevelApi[]): QcLevel[] | undefined =>
    levels?.map((level) => ({
        ordinal: level.ordinal,
        name: level.name,
        defaultMin: level.defaultMin ?? undefined,
        defaultMax: level.defaultMax ?? undefined,
        defaultTarget: level.defaultTarget ?? undefined,
        defaultDeviationPercent: level.defaultDeviationPercent ?? undefined,
        defaultTargetValues: level.defaultTargetValues ?? undefined,
    }));

// Form state allows lotNumberMaxLength to be null while the user is editing the
// input. The save button is gated on validation, so by the time we reach this
// mapper the value is a valid integer; the fallback to DEFAULT_LOT_NUMBER_MAX_LENGTH
// only narrows the type for TypeScript and is unreachable at runtime.
export const mapFormToPayload = (formState: MaterialDefinitionFormState): API.CreateMaterialDefinitionPayload => ({
    name: formState.name.trim(),
    longName: formState.longName.trim() || null,
    provider: formState.provider.trim(),
    type: formState.type,
    deviceTypeId: formState.deviceTypeId,
    testConfigs: formState.testConfigs.map((testConfig) => ({
        testId: testConfig.testId,
        resultType: testConfig.resultType,
        rangeMode: testConfig.rangeMode ?? null,
        ...(testConfig.qcLevels && {
            qcLevels: testConfig.qcLevels.map((level) => mapQcLevelToPayload(level, testConfig.resultType)),
        }),
        ...(testConfig.resultType === 'QUALITATIVE' && { possibleValues: testConfig.possibleValues ?? '' }),
    })),
    lotValidationRules: {
        maxLength: formState.lotNumberMaxLength ?? DEFAULT_LOT_NUMBER_MAX_LENGTH,
        allowAlphanumeric: formState.allowAlphanumericLotNumbers,
    },
});

export const mapMaterialDefinitionFromApiToVm = (data: API.MaterialDefinition): MaterialDefinition => ({
    id: data.id,
    name: data.name,
    longName: data.longName ?? '',
    provider: data.provider,
    type: isMaterialDefinitionType(data.type) ? data.type : 'TEST',
    deviceType: {
        id: data.deviceType.id,
        name: data.deviceType.name,
    },
    testConfigs: data.testConfigs.map((testConfig) => {
        const resultType = isResultType(testConfig.resultType) ? testConfig.resultType : DEFAULT_RESULT_TYPE;
        const storedRangeMode =
            testConfig.rangeMode && isRangeMode(testConfig.rangeMode) ? testConfig.rangeMode : undefined;
        return {
            testId: testConfig.testId,
            testName: testConfig.testName,
            resultType,
            rangeMode: rangeModeForResultType(resultType, storedRangeMode),
            qcLevels: mapQcLevelsFromApi(testConfig.qcLevels),
            possibleValues: testConfig.possibleValues ?? undefined,
        };
    }),
    lotValidationRules: {
        maxLength: data.lotValidationRules.maxLength,
        allowAlphanumeric: data.lotValidationRules.allowAlphanumeric,
    },
    hasLots: data.hasLots ?? false,
});

export const mapMaterialDefinitionsListFromApiToVm = (
    response: API.MaterialDefinitionsListApiResponse
): MaterialDefinition[] => response.data.map(mapMaterialDefinitionFromApiToVm);

export const mapMaterialDefinitionToFormState = (vm: MaterialDefinition): MaterialDefinitionFormState => ({
    name: vm.name,
    longName: vm.longName,
    provider: vm.provider,
    type: vm.type,
    deviceTypeId: vm.deviceType.id,
    testConfigs: vm.testConfigs.map((testConfig) => ({
        clientKey: getUUID(),
        testId: testConfig.testId,
        resultType: testConfig.resultType,
        rangeMode: testConfig.rangeMode ?? null,
        ...(testConfig.qcLevels && { qcLevels: testConfig.qcLevels }),
        ...(testConfig.possibleValues != null && { possibleValues: testConfig.possibleValues }),
    })),
    lotNumberMaxLength: vm.lotValidationRules.maxLength,
    allowAlphanumericLotNumbers: vm.lotValidationRules.allowAlphanumeric,
});
